import React, { useState, useEffect, useCallback } from "react";
import { Gavel, Loader2, Trash2, Sparkles, KeyRound, Check, TrendingUp, Briefcase, ShieldCheck, ExternalLink } from "lucide-react";
import { jevSettingsGet, jevSettingsSave, jevVerdict, jevVerdictList, jevVerdictDelete } from "../lib/api";
import { toast } from "sonner";

const pct = (n) => `${Math.round((n || 0) * 100)}%`;

const choiceStyle = (c) => {
  const v = (c || "").toLowerCase();
  if (["buy", "approve"].includes(v)) return "text-emerald-300 border-emerald-500/40 bg-emerald-500/10";
  if (["sell", "reject"].includes(v)) return "text-rose-300 border-rose-500/40 bg-rose-500/10";
  return "text-amber-300 border-amber-500/40 bg-amber-500/10";
};

const Bar = ({ label, value, tone = "cyan" }) => {
  const tones = { cyan: "bg-[#00F0FF]", emerald: "bg-emerald-400", rose: "bg-rose-400", amber: "bg-amber-400" };
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-cyan-200/60 w-24 shrink-0 truncate capitalize">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className={`h-full ${tones[tone]}`} style={{ width: pct(value) }} />
      </div>
      <span className="text-cyan-100/80 w-10 text-right tabular-nums">{pct(value)}</span>
    </div>
  );
};

const topLevel = (v) => {
  const probs = v.probabilities || {};
  const keys = Object.keys(probs);
  if (!keys.length) return "";
  const top = keys.reduce((a, b) => (probs[a] >= probs[b] ? a : b));
  return (v.legend || {})[top] || "";
};

export const VerdictCard = ({ v }) => {
  if (v.type === "choice") {
    const probs = v.probabilities || {};
    return (
      <div className="bg-[#0a1020] border border-[#00F0FF22] rounded-xl p-3 space-y-2" data-testid="jev-verdict-choice">
        <div className="flex items-center justify-between gap-2">
          <p className="text-cyan-300/60 text-[11px] uppercase tracking-wider font-bold">{v.label}</p>
          {v.confidence != null && <span className="text-cyan-300/50 text-[11px]">confidence {pct(v.confidence)}</span>}
        </div>
        <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold border capitalize ${choiceStyle(v.choice)}`}>{v.choice}</span>
        <div className="space-y-1 pt-1">
          {Object.entries(probs).sort((a, b) => b[1] - a[1]).map(([k, val]) => (
            <Bar key={k} label={k} value={val} tone={k === v.choice ? "cyan" : "cyan"} />
          ))}
        </div>
      </div>
    );
  }
  if (v.type === "score") {
    return (
      <div className="bg-[#0a1020] border border-[#00F0FF22] rounded-xl p-3 space-y-2" data-testid="jev-verdict-score">
        <div className="flex items-center justify-between gap-2">
          <p className="text-cyan-300/60 text-[11px] uppercase tracking-wider font-bold">{v.label}</p>
          {v.confidence != null && <span className="text-cyan-300/50 text-[11px]">confidence {pct(v.confidence)}</span>}
        </div>
        <p className="text-cyan-50 text-sm font-bold">{topLevel(v)}</p>
        <div className="space-y-1 pt-1">
          {Object.entries(v.probabilities || {}).map(([k, val]) => (
            <Bar key={k} label={(v.legend || {})[k] || k} value={val} tone="emerald" />
          ))}
        </div>
      </div>
    );
  }
  // noul
  const yes = v.noul || 0;
  return (
    <div className="bg-[#0a1020] border border-[#00F0FF22] rounded-xl p-3 space-y-2" data-testid="jev-verdict-noul">
      <p className="text-cyan-300/60 text-[11px] uppercase tracking-wider font-bold">{v.label}</p>
      <div className="flex items-center gap-3">
        <span className={`text-lg font-bold ${yes >= 0.5 ? "text-emerald-300" : "text-rose-300"}`}>{yes >= 0.5 ? "Yes" : "No"}</span>
        <div className="flex-1"><Bar label="yes" value={yes} tone={yes >= 0.5 ? "emerald" : "rose"} /></div>
      </div>
    </div>
  );
};

const KeySetup = ({ userId, cfg, onSaved }) => {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!key.trim()) return;
    setBusy(true);
    try { const r = await jevSettingsSave(userId, key.trim()); setKey(""); onSaved(r); toast.success("Jev key saved — verdicts are live"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save key"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-[#060b18] border border-[#00F0FF22] rounded-xl p-3 space-y-2" data-testid="jev-key-setup">
      <div className="flex items-center gap-2">
        <KeyRound className="w-4 h-4 text-cyan-300/60" />
        {cfg?.configured
          ? <span className="text-cyan-100 text-sm flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-300" /> Jev connected <span className="text-cyan-300/40 font-mono text-xs">{cfg.masked}</span></span>
          : <span className="text-amber-200/80 text-xs">No Jev key yet — paste it to turn on verdicts.</span>}
        <a href="https://thejevai.com/settings/apikeys" target="_blank" rel="noreferrer" className="ml-auto text-[#00F0FF] text-[11px] flex items-center gap-1 hover:underline">get a key <ExternalLink className="w-3 h-3" /></a>
      </div>
      <div className="flex items-center gap-2">
        <input value={key} onChange={(e) => setKey(e.target.value)} type="password" placeholder={cfg?.configured ? "replace key (sk_…)" : "paste your Jev key (sk_…)"} data-testid="jev-key-input"
          className="flex-1 bg-[#0a1020] border border-[#00F0FF22] rounded-lg px-3 py-1.5 text-cyan-50 text-sm" />
        <button onClick={save} disabled={busy} data-testid="jev-key-save" className="px-3 py-1.5 rounded-lg text-xs font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
};

export default function NexusVerdict({ session }) {
  const userId = session.user_id;
  const [cfg, setCfg] = useState(null);
  const [kind, setKind] = useState("stock");
  const [state, setState] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([jevSettingsGet(userId), jevVerdictList(userId)]);
      setCfg(c); setItems(l.items || []);
    } catch (e) {}
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const ask = async () => {
    if (!state.trim()) { toast.error("Type your question or paste the details first"); return; }
    setBusy(true);
    try {
      const r = await jevVerdict(userId, state.trim(), kind);
      setItems((h) => [r, ...h]); setState("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't get a verdict"); }
    finally { setBusy(false); }
  };

  const del = async (id) => { try { await jevVerdictDelete(userId, id); setItems((h) => h.filter((x) => x.id !== id)); } catch (e) {} };

  const placeholder = kind === "stock"
    ? "e.g. Should I sell my AAPL calls? NVDA broke out above its 200-day on huge volume after strong earnings — buy the dip?"
    : "e.g. Is this email urgent and should I approve the vendor's new quote? [paste the email / details]";

  return (
    <div className="h-full overflow-y-auto bg-[#030712] p-4 sm:p-6" data-testid="verdict-panel">
      <div className="max-w-3xl mx-auto space-y-5">
        <div>
          <h2 className="text-[#00F0FF] font-bold text-2xl flex items-center gap-2"><Gavel className="w-6 h-6" /> Jev Verdict</h2>
          <p className="text-cyan-200/60 text-sm mt-1">Ask a stock or business question and get an instant, structured decision — <b>buy/sell/hold</b>, conviction, urgency — with real probabilities. Powered by Jev, a decision model that doesn't guess in prose.</p>
        </div>

        <KeySetup userId={userId} cfg={cfg} onSaved={(r) => setCfg(r)} />

        <div className="bg-[#060b18] border border-[#00F0FF33] rounded-2xl p-3 space-y-3">
          <div className="flex items-center gap-2">
            <button onClick={() => setKind("stock")} data-testid="jev-kind-stock"
              className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 border ${kind === "stock" ? "text-[#030712] bg-[#00F0FF] border-[#00F0FF]" : "text-cyan-200/70 border-[#00F0FF33] hover:bg-[#00F0FF11]"}`}><TrendingUp className="w-3.5 h-3.5" /> Stock</button>
            <button onClick={() => setKind("business")} data-testid="jev-kind-business"
              className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 border ${kind === "business" ? "text-[#030712] bg-[#00F0FF] border-[#00F0FF]" : "text-cyan-200/70 border-[#00F0FF33] hover:bg-[#00F0FF11]"}`}><Briefcase className="w-3.5 h-3.5" /> Business</button>
          </div>
          <textarea value={state} onChange={(e) => setState(e.target.value)} rows={3} placeholder={placeholder} data-testid="jev-state-input"
            className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2 text-cyan-50 text-sm outline-none resize-none" />
          <button onClick={ask} disabled={busy} data-testid="jev-ask-btn"
            className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-cyan-300 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Judging…</> : <><Sparkles className="w-4 h-4" /> Get verdict</>}
          </button>
        </div>

        {items.length > 0 ? (
          <div className="space-y-3">
            {items.map((r) => (
              <div key={r.id} className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4 space-y-3" data-testid="jev-verdict-item">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-cyan-100/90 text-sm leading-relaxed flex-1">
                    <span className={`inline-flex items-center gap-1 mr-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${r.kind === "business" ? "bg-purple-500/15 text-purple-200" : "bg-[#00F0FF15] text-[#00F0FF]"}`}>
                      {r.kind === "business" ? <Briefcase className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />} {r.kind}
                    </span>
                    {r.state}
                  </p>
                  <button onClick={() => del(r.id)} data-testid="jev-verdict-delete" className="text-cyan-200/40 hover:text-rose-300 shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid sm:grid-cols-3 gap-2.5">
                  {(r.verdicts || []).map((v) => <VerdictCard key={v.id} v={v} />)}
                </div>
                <p className="text-cyan-300/30 text-[10px] flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Jev decision · research signal, not financial advice</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-cyan-200/40" data-testid="verdict-empty">
            <Gavel className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No verdicts yet. Ask a stock or business question above for an instant structured judgment.</p>
          </div>
        )}
      </div>
    </div>
  );
}
